import React, { useState } from "react";
import {
  ConflictFieldRow,
  ConflictModal,
  ConfirmModal,
  InfoModal,
} from "../../components/ui/Modal";
import Navbar from "../../components/ui/navbar";
import AuditTab from "./AuditTab";
import DataEditTab from "./DataEditTab";
import DataImportTab from "./DataImportTab";
import MigrationTab from "./MigrationTab";
import MyUploadsTab from "./MyUploadsTab";
import UsersTab from "./UsersTab";
import useAuthStore from "../../store/useAuthStore";

type TabKey = "users" | "audit" | "import" | "my_uploads" | "edit" | "migration";

interface ConfirmState {
  open: boolean;
  title: string;
  message: React.ReactNode;
  confirmLabel: string;
  cancelLabel?: string;
  variant: "primary" | "danger" | "warning";
  onConfirm: () => void;
  onCancel?: () => void;
}

interface InfoState {
  open: boolean;
  title: string;
  message: React.ReactNode;
  variant: "success" | "info" | "warning";
}

interface ConflictState {
  open: boolean;
  rowKey: string | number;
  rows: ConflictFieldRow[];
  onAcceptServer: () => void;
  onOverwrite: () => void;
}

const CONFIRM_DEFAULT: ConfirmState = {
  open: false,
  title: "",
  message: "",
  confirmLabel: "Confirm",
  cancelLabel: "Cancel",
  variant: "primary",
  onConfirm: () => undefined,
};

const INFO_DEFAULT: InfoState = {
  open: false,
  title: "",
  message: "",
  variant: "success",
};

const CONFLICT_DEFAULT: ConflictState = {
  open: false,
  rowKey: "",
  rows: [],
  onAcceptServer: () => undefined,
  onOverwrite: () => undefined,
};

const SettingsPage: React.FC = () => {
  const user = useAuthStore((s) => s.user);
  const isAdmin = user?.role === "admin" || user?.is_superuser;
  const defaultTab: TabKey = isAdmin ? "users" : "edit";

  const [activeTab, setActiveTab] = useState<TabKey>(defaultTab);
  const [confirmState, setConfirmState] = useState<ConfirmState>(CONFIRM_DEFAULT);
  const [infoState, setInfoState] = useState<InfoState>(INFO_DEFAULT);
  const [conflictState, setConflictState] = useState<ConflictState>(CONFLICT_DEFAULT);

  const showConfirm = React.useCallback((cfg: Parameters<React.ComponentProps<typeof UsersTab>["showConfirm"]>[0]) => {
    setConfirmState({
      open: true,
      title: cfg.title,
      message: cfg.message,
      confirmLabel: cfg.confirmLabel,
      cancelLabel: cfg.cancelLabel,
      variant: cfg.variant ?? "primary",
      onConfirm: () => {
        setConfirmState((prev) => ({ ...prev, open: false }));
        cfg.onConfirm();
      },
      onCancel: () => {
        setConfirmState((prev) => ({ ...prev, open: false }));
        if (cfg.onCancel) cfg.onCancel();
      },
    });
  }, []);

  const showInfo = React.useCallback((cfg: Parameters<React.ComponentProps<typeof UsersTab>["showInfo"]>[0]) => {
    setInfoState({
      open: true,
      title: cfg.title,
      message: cfg.message,
      variant: cfg.variant ?? "success",
    });
  }, []);

  const closeConfirm = React.useCallback(() => setConfirmState((prev) => ({ ...prev, open: false })), []);
  const closeInfo = React.useCallback(() => setInfoState((prev) => ({ ...prev, open: false })), []);

  const showConflict = React.useCallback((cfg: {
    rowKey: string | number;
    rows: ConflictFieldRow[];
    onAcceptServer: () => void;
    onOverwrite: () => void;
  }) => {
    setConflictState({
      open: true,
      rowKey: cfg.rowKey,
      rows: cfg.rows,
      onAcceptServer: cfg.onAcceptServer,
      onOverwrite: cfg.onOverwrite,
    });
  }, []);

  const closeConflict = React.useCallback(() => setConflictState((prev) => ({ ...prev, open: false })), []);

  return (
    <>
      <div className="relative flex-shrink-0 w-full py-8 border-b border-1">
        <Navbar />
      </div>
      <div className="bg-gray-50 min-h-[calc(100vh-4rem)]">
        <div className="w-full px-3 sm:px-4 md:px-6 py-4">
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-gray-900">Settings</h2>
            <p className="mt-1 text-sm text-gray-500">
              Manage team members, roles, and audit activity.
            </p>
          </div>

          <div className="mb-6 border-b border-gray-200">
            <nav className="flex -mb-px gap-6">
              {isAdmin && (
                <TabButton
                  label="User management"
                  active={activeTab === "users"}
                  onClick={() => setActiveTab("users")}
                />
              )}
              <TabButton
                label="Data import"
                active={activeTab === "import"}
                onClick={() => setActiveTab("import")}
              />
              <TabButton
                label="My Uploads"
                active={activeTab === "my_uploads"}
                onClick={() => setActiveTab("my_uploads")}
              />
              <TabButton
                label="Data edit"
                active={activeTab === "edit"}
                onClick={() => setActiveTab("edit")}
              />
              <TabButton
                label="Migration"
                active={activeTab === "migration"}
                onClick={() => setActiveTab("migration")}
              />
              {isAdmin && (
                <TabButton
                  label="Audit log"
                  active={activeTab === "audit"}
                  onClick={() => setActiveTab("audit")}
                />
              )}
            </nav>
          </div>

          {activeTab === "users" && (
            <UsersTab showConfirm={showConfirm} showInfo={showInfo} />
          )}
          {activeTab === "import" && (
            <DataImportTab showConfirm={showConfirm} showInfo={showInfo} navigateTab={setActiveTab} />
          )}
          {activeTab === "my_uploads" && (
            <MyUploadsTab showConfirm={showConfirm} showInfo={showInfo} />
          )}
          {activeTab === "edit" && (
            <DataEditTab
              showConfirm={showConfirm}
              showInfo={showInfo}
              showConflict={showConflict}
              closeConflict={closeConflict}
            />
          )}
          {activeTab === "migration" && (
            <MigrationTab
              active={activeTab === "migration"}
              showConfirm={showConfirm}
              showInfo={showInfo}
            />
          )}
          {activeTab === "audit" && (
            <AuditTab active={activeTab === "audit"} />
          )}
        </div>
      </div>

      <ConfirmModal
        open={confirmState.open}
        title={confirmState.title}
        message={confirmState.message}
        confirmLabel={confirmState.confirmLabel}
        cancelLabel={confirmState.cancelLabel}
        variant={confirmState.variant}
        onConfirm={confirmState.onConfirm}
        onCancel={confirmState.onCancel ?? closeConfirm}
      />
      <InfoModal
        open={infoState.open}
        title={infoState.title}
        message={infoState.message}
        variant={infoState.variant}
        onClose={closeInfo}
      />
      <ConflictModal
        open={conflictState.open}
        rowKey={conflictState.rowKey}
        rows={conflictState.rows}
        onAcceptServer={conflictState.onAcceptServer}
        onOverwrite={conflictState.onOverwrite}
        onCancel={closeConflict}
      />
    </>
  );
};

const TabButton: React.FC<{
  label: string;
  active: boolean;
  onClick: () => void;
}> = ({ label, active, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`pb-3 text-sm font-medium border-b-2 ${
      active
        ? "border-blue-600 text-blue-600"
        : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
    }`}
  >
    {label}
  </button>
);

export default SettingsPage;
